// ============================================
// CHÁMOMILE - Private Event Inquiry Service
// ============================================
// Bulk online orders are self-serve (existing checkout + bulk discount).
// Private events are negotiated, so this deliberately does NOT try to build a
// quote/deposit checkout flow. It captures just enough to quote from, saves it
// so nothing is lost if the customer doesn't actually hit "send" in WhatsApp,
// and hands the actual negotiation off to a human on WhatsApp — pre-filled so
// staff aren't starting from a blank chat.
//
// Once terms are agreed, staff create a one-time promo code (existing Promos
// module) and the customer completes their order through the normal checkout.
// No parallel payment flow needed.

import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../config/firebase';

export const EVENT_TYPES = ['Corporate', 'Wedding', 'Birthday', 'Community', 'Other'] as const;
export type EventType = typeof EVENT_TYPES[number];

// The business WhatsApp number — matches the "Contact Us" link and Privacy
// Policy contact info already used elsewhere in this app.
const BUSINESS_WHATSAPP_NUMBER = '60108202187';

export interface EventInquiryInput {
  eventDate: string;       // 'YYYY-MM-DD' or '' if "not sure yet"
  headcount: string;       // free text — "50" or "~80" or "30-40"
  eventType: EventType | '';
  contactName: string;
  contactPhone: string;
  notes?: string;
  /** Customer is interested in renting the HCK chiller (priced during the quote). */
  wantsFridgeRental?: boolean;
}

/**
 * Photos of past events, shown in the inquiry form as social proof.
 * Settings-managed (`settings/eventGallery.images`) so photos can be swapped
 * without a code change — same pattern as category images.
 */
export const fetchEventGallery = async (): Promise<string[]> => {
  if (!isFirebaseConfigured() || !db) return [];
  try {
    const snap = await getDoc(doc(db, 'settings', 'eventGallery'));
    if (!snap.exists()) return [];
    const images = snap.data().images;
    return Array.isArray(images) ? images.filter((u: any) => typeof u === 'string' && u.trim()) : [];
  } catch (error) {
    console.error('[EventInquiryService] Error fetching event gallery:', error);
    return [];
  }
};

const cleanPhone = (phone: string): string => phone.replace(/\D/g, '');

/**
 * Build the WhatsApp message staff receive — a ready summary, not a blank
 * "hi do you do events?" they have to interview the customer to get.
 */
export const buildEventInquiryMessage = (input: EventInquiryInput): string => {
  const lines = [
    `Hi! I'd like to enquire about a private event with Chámomile. 🍵`,
    '',
    `*Event type:* ${input.eventType || 'Not specified'}`,
    `*Date:* ${input.eventDate || 'Not sure yet'}`,
    `*Estimated headcount:* ${input.headcount || 'Not specified'}`,
    `*Name:* ${input.contactName}`,
  ];

  if (input.wantsFridgeRental) {
    lines.push(`*Chiller rental:* Yes — interested in renting the HCK fridge`);
  }

  if (input.notes && input.notes.trim()) {
    lines.push('', `*Notes:* ${input.notes.trim()}`);
  }

  return lines.join('\n');
};

export const buildEventInquiryWhatsAppUrl = (input: EventInquiryInput): string => {
  const message = buildEventInquiryMessage(input);
  return `https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
};

/**
 * Save the inquiry to Firestore so it becomes a lead in the POS regardless of
 * whether the customer actually sends the WhatsApp message.
 */
export const submitEventInquiry = async (
  input: EventInquiryInput
): Promise<{ success: boolean; id?: string; error?: string }> => {
  if (!input.contactName?.trim()) return { success: false, error: 'Name is required' };
  if (!cleanPhone(input.contactPhone || '')) return { success: false, error: 'Phone number is required' };

  if (!isFirebaseConfigured() || !db) {
    // No offline fallback for leads — an inquiry that only exists in
    // localStorage on the customer's device is not a lead anyone can act on.
    return { success: false, error: 'Could not save your inquiry right now. Please try WhatsApp directly.' };
  }

  try {
    const ref = await addDoc(collection(db, 'event_inquiries'), {
      eventDate: input.eventDate || '',
      headcount: (input.headcount || '').trim(),
      eventType: input.eventType || '',
      contactName: input.contactName.trim(),
      contactPhone: cleanPhone(input.contactPhone),
      notes: (input.notes || '').trim(),
      wantsFridgeRental: input.wantsFridgeRental === true,
      status: 'new', // new | quoted | confirmed | declined
      source: 'online-app',
      createdAt: serverTimestamp(),
    });
    return { success: true, id: ref.id };
  } catch (error: any) {
    console.error('[EventInquiryService] Error saving inquiry:', error);
    return { success: false, error: 'Could not save your inquiry. Please try WhatsApp directly.' };
  }
};

export default { EVENT_TYPES, buildEventInquiryMessage, buildEventInquiryWhatsAppUrl, submitEventInquiry, fetchEventGallery };
